"use client";

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useSidebar } from '@/app/context/SidebarContext';
import DashboardIcon from '../../../../../public/images/icons/dashboard.svg';
import CalendarIcon from '../../../../../public/images/icons/calendar.svg';
import PlusIcon from '../../../../../public/images/icons/plus.svg';
import DirectoryIcon from '../../../../../public/images/icons/directory.svg';
import RecentIcon from '../../../../../public/images/icons/recent-activity.svg';
import HourglassIcon from '../../../../../public/images/icons/hourglass.svg';
import CheckInIcon from '../../../../../public/images/icons/check-in.svg';
import TeamsIcon from '../../../../../public/images/icons/teams.svg';
import OverviewIcon from '../../../../../public/images/icons/overview.svg';
import BuildingIcon from '../../../../../public/images/icons/building.svg';
import VolunteersIcon from '../../../../../public/images/icons/volunteers.svg';
import RegistrationIcon from '../../../../../public/images/icons/registration.svg';
import FormsIcon from '../../../../../public/images/icons/list.svg';
import ToolsIcon from '../../../../../public/images/icons/tools.svg';
import CreateEventIcon from '../../../../../public/images/icons/create-event.svg';
import UpcomingEventsIcon from '../../../../../public/images/icons/upcoming-events.svg';
import PeopleIcon from '../../../../../public/images/icons/people.svg';
import AddPersonIcon from '../../../../../public/images/icons/add-person.svg';

/*
 * Flat navigation — every item is a top-level link with its own icon.
 * Items are grouped by section with a label divider when expanded.
 * No nesting, no accordions.
 */
const NAV_SECTIONS = [
  {
    label: null, // No divider for top-level
    items: [
      { label: 'Dashboard', href: '/dashboard', Icon: DashboardIcon },
    ],
  },
  {
    label: 'Events',
    items: [
      { label: 'All Events', href: '/events', Icon: CalendarIcon, exact: true },
      { label: 'Create Event', href: '/events?action=createEvent', Icon: CreateEventIcon },
      { label: 'Active Events', href: '/events?status=ACTIVE', Icon: OverviewIcon },
      { label: 'Upcoming', href: '/events?status=PUBLISHED', Icon: UpcomingEventsIcon },
      { label: 'Past Events', href: '/events?status=COMPLETED', Icon: HourglassIcon },
    ],
  },
  {
    label: 'People',
    items: [
      { label: 'All People', href: '/people', Icon: PeopleIcon, exact: true },
      { label: 'Add Person', href: '/people?action=createPerson', Icon: AddPersonIcon },
    ],
  },
  {
    label: 'Event Tools',
    items: [
      { label: 'Badge Generator', href: '/tools/badge-generator', Icon: ToolsIcon },
      // { label: 'Check-in', href: '/events?tab=checkins', Icon: CheckInIcon },
      // { label: 'Teams', href: '/events?tab=teams', Icon: TeamsIcon },
      // { label: 'Locations', href: '/events?tab=locations', Icon: BuildingIcon },
      // { label: 'Volunteers', href: '/events?tab=volunteers', Icon: VolunteersIcon },
      // { label: 'Registration', href: '/events?tab=registration', Icon: RegistrationIcon },
      { label: 'Form Templates', href: '/forms', Icon: FormsIcon },
    ],
  },
];

const SidebarNavigation = () => {
  const pathname = usePathname();
  const { isCollapsed } = useSidebar();

  const isActive = (item) => {
    // For items with query params, match the full href
    if (item.href.includes('?')) {
      return typeof window !== 'undefined' &&
        window.location.pathname + window.location.search === item.href;
    }
    // Exact match for items flagged as such
    if (item.exact) return pathname === item.href;
    // Prefix match
    return pathname === item.href || pathname.startsWith(item.href + '/');
  };

  return (
    <nav className="sidebar-nav" aria-label="Primary">
      {NAV_SECTIONS.map((section, sIdx) => (
        <div key={sIdx} className="sidebar-section">
          {/* Section divider label — only visible when expanded */}
          {section.label && !isCollapsed && (
            <div className="sidebar-section-label">{section.label}</div>
          )}
          {section.label && isCollapsed && (
            <div className="sidebar-section-divider" />
          )}

          <ul className="sidebar-nav-list">
            {section.items.map((item) => (
              <li key={item.href + item.label}>
                <Link
                  href={item.href}
                  className={`sidebar-nav-link ${isActive(item) ? 'active' : ''}`}
                  {...(isCollapsed ? {
                    'data-bs-toggle': 'tooltip',
                    'data-bs-placement': 'right',
                    title: item.label,
                  } : {})}
                >
                  <span className="sidebar-nav-icon">
                    <item.Icon className="icon" aria-hidden="true" focusable="false" />
                  </span>
                  <span className="sidebar-nav-label">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
};

export default SidebarNavigation;
